import { getTaskById } from "../apis/tasksApis/getTaskById";
import { getPropertyDetails } from "../apis/propertiesApis/getPropertyDetails";
import { resolveTaskPriorityPayload } from "../utils/taskPriority";

export function normalizeTaskType(raw) {
  return raw === "recurring" ? "recurring" : "single";
}

/** Ensures phase / sub_phase objects exist when API returns only *_id fields. */
export function normalizeTaskShape(task) {
  if (task == null || typeof task !== "object") return task;

  const phase =
    task.phase != null && typeof task.phase === "object"
      ? task.phase
      : task.phase_id != null
        ? { id: task.phase_id, name: task.phase_name ?? null }
        : null;

  const subPhase =
    task.sub_phase != null && typeof task.sub_phase === "object"
      ? task.sub_phase
      : task.sub_phase_id != null
        ? { id: task.sub_phase_id, name: task.sub_phase_name ?? null }
        : null;

  return {
    ...task,
    phase,
    sub_phase: subPhase,
    assigned_to:
      task.assigned_to ??
      (task.assigned_to_contact_id != null
        ? { id: task.assigned_to_contact_id }
        : null),
  };
}

export function resolveTaskPropertyId(task) {
  const id = task?.property_id ?? task?.property?.id;
  if (id == null || String(id).trim() === "") return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export function resolvePropertyTypeIdFromTask(task) {
  const raw =
    task?.property_type_id ??
    task?.property?.type?.id ??
    task?.property_type?.id;
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Loads project type id for task composer (phases dropdown).
 * @param {number} propertyId
 */
export async function fetchPropertyTypeIdForProperty(propertyId) {
  const res = await getPropertyDetails(propertyId);
  if (res.error_code !== 1) {
    return {
      propertyTypeId: null,
      errorMessage: res.error_message ?? "Could not load project details.",
    };
  }
  const data = res.res?.data ?? res.res;
  const propertyTypeId = resolvePropertyTypeIdFromTask({ property: { type: data?.type } });
  if (propertyTypeId == null) {
    return {
      propertyTypeId: null,
      errorMessage: "Could not determine project type for this task.",
    };
  }
  return { propertyTypeId, errorMessage: null };
}

/**
 * Resolves task, property id, and property type id for opening the edit composer.
 * @param {object} task
 */
export async function resolveTaskForEditComposer(task) {
  const { task: taskForEdit, errorMessage: taskError } = await resolveTaskForStatusUpdate(task);
  if (taskError || !taskForEdit) {
    return {
      task: null,
      propertyId: null,
      propertyTypeId: null,
      errorMessage: taskError ?? "Could not load task details.",
    };
  }

  const propertyId = resolveTaskPropertyId(taskForEdit);
  if (propertyId == null) {
    return {
      task: null,
      propertyId: null,
      propertyTypeId: null,
      errorMessage: "This task is missing a project. Cannot edit.",
    };
  }

  let propertyTypeId = resolvePropertyTypeIdFromTask(taskForEdit);
  if (propertyTypeId == null) {
    const fetched = await fetchPropertyTypeIdForProperty(propertyId);
    if (fetched.errorMessage) {
      return {
        task: null,
        propertyId: null,
        propertyTypeId: null,
        errorMessage: fetched.errorMessage,
      };
    }
    propertyTypeId = fetched.propertyTypeId;
  }

  return { task: taskForEdit, propertyId, propertyTypeId, errorMessage: null };
}

/**
 * @returns {string|null} Error message when status cannot be toggled, else null.
 */
export function validateTaskForStatusUpdate(task, propertyIdOverride = null) {
  const normalized = normalizeTaskShape(task);
  if (normalized?.phase?.id == null || normalized?.sub_phase?.id == null) {
    return "This task data is incomplete. Edit the task before changing status.";
  }
  if (normalized?.start_date == null) {
    return "This task data is incomplete. Edit the task before changing status.";
  }
  if (normalized?.assigned_to?.id == null) {
    return "This task data is incomplete. Edit the task before changing status.";
  }
  const propertyId =
    propertyIdOverride != null && String(propertyIdOverride).trim() !== ""
      ? Number(propertyIdOverride)
      : resolveTaskPropertyId(normalized);
  if (propertyId == null || !Number.isFinite(propertyId)) {
    return "This task is missing a project. Edit the task before changing status.";
  }
  return null;
}

/**
 * Loads full task details when assigned-tasks rows omit fields required for status updates.
 * @param {object} task
 * @returns {Promise<{ task: object|null, errorMessage: string|null }>}
 */
export async function resolveTaskForStatusUpdate(task) {
  const normalized = normalizeTaskShape(task);
  if (validateTaskForStatusUpdate(normalized) === null) {
    return { task: normalized, errorMessage: null };
  }

  const res = await getTaskById(task.id);
  if (res.error_code !== 1) {
    return {
      task: null,
      errorMessage: res.error_message ?? "Could not load task details.",
    };
  }

  const body = res.res?.data ?? res.res;
  const full = normalizeTaskShape({ ...task, ...(body ?? {}) });
  const validationError = validateTaskForStatusUpdate(full);
  if (validationError) {
    return { task: null, errorMessage: validationError };
  }

  return { task: full, errorMessage: null };
}

/**
 * @param {object} task
 * @param {boolean} checked
 * @param {string|number} userId
 * @param {string|number|null} [propertyIdOverride] Used on property details when task row omits property_id.
 */
export function buildTaskStatusUpdatePayload(
  task,
  checked,
  userId,
  propertyIdOverride = null,
) {
  const normalized = normalizeTaskShape(task);
  const tt = normalizeTaskType(normalized.task_type);
  const propertyId =
    propertyIdOverride != null && String(propertyIdOverride).trim() !== ""
      ? Number(propertyIdOverride)
      : resolveTaskPropertyId(normalized);
  return {
    task_id: normalized.id,
    property_id: propertyId,
    phase_id: normalized.phase.id,
    sub_phase_id: normalized.sub_phase.id,
    start_date: normalized.start_date,
    end_date: normalized.end_date,
    updated_by_user_id: Number(userId),
    location: normalized.location ?? null,
    description: normalized.description ?? null,
    status: checked ? "completed" : "not_completed",
    link: normalized.link ?? null,
    comment: normalized.comment ?? null,
    assigned_to_contact_id: Number(normalized.assigned_to.id),
    priority: resolveTaskPriorityPayload(normalized.priority),
    task_type: tt,
    frequency:
      tt === "recurring" &&
      normalized.frequency != null &&
      String(normalized.frequency).trim()
        ? String(normalized.frequency).trim()
        : null,
  };
}
